import React, {
  useCallback,
  useEffect,
  useState,
  useMemo,
  useRef,
} from "react";
import axios from "axios";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Spinner from "react-bootstrap/Spinner";
import { isMobileOrTablet } from "../../Utils";
import "../index.css";
import ComicBubble from "./CommicBubble";
import ConsentSection from "./ConsentSection";
import "./SearchBar.css";

// Extracted helper functions outside the component to avoid recreating them
const isIOS = () => {
  return (
    [
      "iPad Simulator",
      "iPhone Simulator",
      "iPod Simulator",
      "iPad",
      "iPhone",
      "iPod",
    ].includes(navigator.platform) ||
    (navigator.userAgent.includes("Mac") && "ontouchend" in document)
  );
};

// Helper function to check if input is the default text or a part of it
const isDefaultTextOrPart = (inputValue, defaultText) => {
  if (!inputValue) return true;

  // Check if input is the default text or a progressive part of it
  return defaultText.startsWith(inputValue) || inputValue === defaultText;
};

const SearchBar = ({ screenWidth, angelaiUrl }) => {
  const inputRef = useRef(null);
  const userInteractedRef = useRef(false);
  const animationTimeoutRef = useRef(null);
  const resetTimeoutRef = useRef(null);

  // Cache device detection results
  const isiOSDevice = useMemo(() => isIOS(), []);
  const isMobile = useMemo(() => isMobileOrTablet(), []);
  // Consolidate duplicate environment variables into a single reference
  const imgUrl = useMemo(
    () => process.env.REACT_APP_CDN_WEB + "angelai-new-design/",
    []
  );

  // Core state
  const [isFocused, setIsFocused] = useState(false);
  const [showComicBubble, setShowComicBubble] = useState(true);
  const [animationPhase, setAnimationPhase] = useState(0); // 0: not running, 1-typingText.length: running
  const [checkboxChecked, setCheckboxChecked] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [hashkey, setHashkey] = useState("");

  // Constants
  const typingText = useMemo(() => "I want a low rate", []);

  // Animation function definition
  const startTypingAnimation = useCallback(() => {
    // Clear any existing animation
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
      animationTimeoutRef.current = null;
    }
    if (resetTimeoutRef.current) {
      clearTimeout(resetTimeoutRef.current);
      resetTimeoutRef.current = null;
    }

    setAnimationPhase(0);

    const runAnimation = (phase) => {
      // Cancel animation if user is typing
      if (inputRef.current && inputRef.current._isTyping) {
        return;
      }

      if (phase <= typingText.length) {
        setAnimationPhase(phase);
        animationTimeoutRef.current = setTimeout(() => {
          // Check again before proceeding
          if (inputRef.current && inputRef.current._isTyping) {
            return;
          }
          runAnimation(phase + 1);
        }, 150);
      } else {
        // Reset animation after completion
        resetTimeoutRef.current = setTimeout(() => {
          // Only restart if not typing
          if (!(inputRef.current && inputRef.current._isTyping)) {
            setAnimationPhase(0);
            startTypingAnimation();
          }
        }, 1000);
      }
    };

    // Start the animation
    runAnimation(1);
  }, [typingText]);

  // Initialize on first load - runs only once
  useEffect(() => {
    userInteractedRef.current = false;

    // Start animation only once on initial render if needed
    if (!isFocused && !inputRef.current?.value) {
      const timer = setTimeout(() => startTypingAnimation(), 0);
      return () => clearTimeout(timer);
    }

    // Cleanup function
    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
      if (resetTimeoutRef.current) {
        clearTimeout(resetTimeoutRef.current);
      }
    };
  }, []); // Empty dependency array ensures this only runs once

  // Handle click outside the search bar
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!isFocused) return;

      const isOutsideInput =
        inputRef.current && !inputRef.current.contains(event.target);
      const isOutsideConsent =
        !event.target.closest(".consent-section-input") &&
        !event.target.closest(".consent-part");

      // Check if click is outside the comic bubble as well
      const isOutsideComicBubble = !event.target.closest(
        ".comic-bubble-component"
      );

      if (isOutsideInput && isOutsideConsent && isOutsideComicBubble) {
        setIsFocused(false);

        // Get current input value
        const currentInputValue = inputRef.current?.value || "";

        // Check if input is default text or part of it
        const isDefaultOrPart = isDefaultTextOrPart(
          currentInputValue,
          typingText
        );

        // Only reset input and show comic bubble if it's the default text or part of it
        if (isDefaultOrPart) {
          if (inputRef.current) {
            inputRef.current.value = "";
          }
          setShowComicBubble(true);
          startTypingAnimation();
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isFocused, typingText, startTypingAnimation]);

  // Handle notification dismiss
  useEffect(() => {
    if (showNotification) {
      const handleGlobalClick = () => {
        setShowNotification(false);
      };

      document.addEventListener("mousedown", handleGlobalClick);
      return () => {
        document.removeEventListener("mousedown", handleGlobalClick);
      };
    }
  }, [showNotification]);

  // Memoize the toggle function
  const toggleExpand = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  // Optimized handlers
  const handleFocus = useCallback(() => {
    // Stop any running animation
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
      animationTimeoutRef.current = null;
    }
    if (resetTimeoutRef.current) {
      clearTimeout(resetTimeoutRef.current);
      resetTimeoutRef.current = null;
    }

    // Hide notifications
    if (showNotification) {
      setShowNotification(false);
    }

    if (!isFocused) {
      setIsFocused(true);

      if (expanded) {
        setExpanded(false);
      }

      setShowComicBubble(false);

      // Set default text only on first interaction with empty field
      if (!inputRef.current.value && !userInteractedRef.current) {
        userInteractedRef.current = true;
        inputRef.current.value = typingText;

        // Defer cursor positioning to next frame for better performance
        requestAnimationFrame(() => {
          if (inputRef.current) {
            const length = typingText.length;
            inputRef.current.selectionStart = length;
            inputRef.current.selectionEnd = length;
          }
        });
      }
    }
  }, [isFocused, expanded, showNotification, typingText]);

  const handleBlur = useCallback(() => {
    if (
      !document.activeElement ||
      (document.activeElement !== inputRef.current &&
        !document.activeElement.closest(".consent-part"))
    ) {
      setIsFocused(false);

      if (!inputRef.current.value) {
        setShowComicBubble(true);
        startTypingAnimation();
        userInteractedRef.current = false;
      }
    }
  }, [startTypingAnimation]);

  const handleSubmit = useCallback(async () => {
    const submittedText =
      isFocused || inputRef.current?.value
        ? inputRef.current?.value || typingText
        : inputRef.current?.value;

    if (!submittedText) {
      setShowNotification(true);
      return;
    }

    setIsLoading(true);
    const windowReference = window.open();

    try {
      const workflowKeyResponse = await axios.post(
        process.env.REACT_APP_CDN_WORKFLOW_API,
        { message: submittedText, workflow: "workflow" }
      );

      const hashKey = workflowKeyResponse.data.key;
      setHashkey(hashKey);

      const url = `${process.env.REACT_APP_CDN_ANGELAI_URL}${hashKey}`;
      windowReference.location = url;

      // Clear the input directly using the ref instead of state
      if (inputRef.current) {
        inputRef.current.value = "";
      }
      userInteractedRef.current = false;
      setShowComicBubble(true);
      startTypingAnimation();
    } catch (error) {
      console.error("Error fetching workflow key:", error);
      if (windowReference) {
        windowReference.close();
      }
      alert(
        "Something went wrong while processing your request. Please try again later."
      );
    } finally {
      setIsLoading(false);
    }
  }, [isFocused, typingText, startTypingAnimation]);

  // Memoized props for ComicBubble
  const comicBubbleProps = useMemo(
    () => ({
      showComicBubble,
      expanded,
      toggleExpand,
      isMobile,
      typingText,
      animationPhase,
    }),
    [
      showComicBubble,
      expanded,
      toggleExpand,
      isMobile,
      typingText,
      animationPhase,
    ]
  );

  // Create a stable checkbox setter callback
  const handleCheckboxChange = useCallback((value) => {
    setCheckboxChecked(value);
  }, []);

  // Memoized props for ConsentSection
  const consentSectionProps = useMemo(
    () => ({
      isMobile,
      checkboxChecked,
      setCheckboxChecked: handleCheckboxChange,
    }),
    [isMobile, checkboxChecked, handleCheckboxChange]
  );

  // Get the input value reference for child components
  const getInputValue = useCallback(() => inputRef.current?.value || "", []);

  // Memoized placeholder text component for animation
  const PlaceholderText = useMemo(() => {
    if (!checkboxChecked || isFocused || inputRef.current?.value) {
      return null;
    }

    return (
      <div
        className="placeholder-wrapper"
        style={{
          pointerEvents: "none",
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
        }}
      >
        <span style={{ display: "flex" }}>
          {animationPhase > 0 ? typingText.slice(0, animationPhase) : ""}
          <span className="cursor-animation">|</span>
        </span>
      </div>
    );
  }, [checkboxChecked, isFocused, animationPhase, typingText]);

  // Effect to maintain focus and cursor position
  useEffect(() => {
    if (isFocused && inputRef.current) {
      // If element should be focused but isn't, focus it
      if (document.activeElement !== inputRef.current) {
        inputRef.current.focus();
      }

      // Position cursor at end of text when focused
      const textLength = inputRef.current.value.length;
      inputRef.current.selectionStart = textLength;
      inputRef.current.selectionEnd = textLength;
    }
  }, [isFocused]);

  // Memoized input component
  const OptimizedTextArea = useMemo(() => {
    return (
      <form
        className="input-wrapper"
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <img
          src={imgUrl + "search-bar-angel-gif.gif"}
          alt="Search GIF"
          className="search-gif"
        />
        <textarea
          style={{
            // Add this conditional style to prevent iOS zoom
            fontSize: "16px",
            minHeight: "48px",
            minWidth: "100%", // Changed from '48px' to full width
            boxSizing: "border-box",
          }}
          className={`search-input non-resizable-textarea ${
            !checkboxChecked ? "disabled" : ""
          }`}
          ref={inputRef}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={!checkboxChecked}
          aria-label="Search"
          aria-labelledby="search"
          rows={1}
        />

        {/* Button for typing animation positioned absolutely */}
        <button
          type="button"
          className="animation-button"
          style={{
            display: !inputRef.current?.value && !isFocused ? "flex" : "none",
            position: "absolute",
            top: "0",
            left: "0",
            right: "0",
            bottom: "0",
            minWidth: "48px",
            minHeight: "48px",
            padding: "12px",
            background: "transparent",
            border: "none",
            cursor: "pointer",
            alignItems: "center",
            justifyContent: "flex-start",
            paddingLeft: "16px", // Align with textarea padding
          }}
          onClick={() => {
            if (inputRef.current) {
              inputRef.current.value = typingText;
              inputRef.current.focus();
            }
          }}
        >
          {PlaceholderText}
        </button>
      </form>
    );
  }, [
    handleFocus,
    handleBlur,
    checkboxChecked,
    PlaceholderText,
    isFocused,
    typingText,
    handleSubmit,
    imgUrl,
  ]);

  // Memoize container style to prevent re-renders
  const containerStyle = useMemo(
    () => ({
      height:
        expanded && !isFocused
          ? isMobile
            ? "23.5rem"
            : "23rem"
          : isMobile
          ? "9.5rem"
          : "9.5rem",
      maxWidth: isMobile ? "340px" : "510px",
    }),
    [expanded, isFocused, isMobile]
  );

  // Memoize consent part style to prevent re-renders
  const consentPartStyle = useMemo(() => {
    const shouldShowWidth =
      (isMobile && (isFocused || inputRef.current?.value)) ||
      (!isMobile && inputRef.current?.value);

    return {
      width: shouldShowWidth ? "auto" : "0",
      borderRadius:
        !isMobile && inputRef.current?.value
          ? "0 0 50% 50%"
          : isMobile && (isFocused || inputRef.current?.value)
          ? "0 0 50% 50%"
          : "0",
      backgroundColor: inputRef.current?.value ? "#f0f3f4" : "transparent",
      padding: isMobile ? "0px 5px 45px 2px" : "0px 5px",
      marginTop:
        window.innerWidth >= 768 && window.innerWidth <= 991
          ? "6.2rem"
          : isMobile
          ? "6.3rem"
          : "6.8rem",
      paddingTop:
        window.innerWidth >= 768 && window.innerWidth <= 991
          ? "25px"
          : isMobile
          ? "18px"
          : "11px",
      transition: "all 0.3s ease",
      opacity: "1",
      pointerEvents: "auto",
      width: "auto",
      height: isMobile ? "50px" : "50px",
    };
  }, [isMobile, isFocused]);

  return (
    <div className="search-bar-container">
      <Container
        className={`searchbar-container ${
          expanded && !isFocused ? "expanded" : ""
        }`}
        style={containerStyle}
      >
        <Row>
          <Col>
            {/* Comic bubble */}
            <ComicBubble {...comicBubbleProps} angelaiUrl={angelaiUrl} />

            {/* Consent section - using memoized props to avoid re-rendering parent */}
            <ConsentSection
              {...consentSectionProps}
              inputValue={getInputValue()}
            />

            {/* Search input and button */}
            <div
              className="input-and-btn-div d-flex"
              style={{
                justifyContent: "center",
                maxWidth: "550px",
                margin: "auto",
                marginTop: "1.5rem",
                position: "relative",
              }}
            >
              {/* Notification bubble */}
              {showNotification && (
                <div className="notification-bubble">
                  <svg
                    className="notification-alert-icon"
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M9.95542 17.8967C14.2822 17.8967 17.7897 14.3891 17.7897 10.0624C17.7897 5.73557 14.2822 2.22803 9.95542 2.22803C5.62864 2.22803 2.12109 5.73557 2.12109 10.0624C2.12109 14.3891 5.62864 17.8967 9.95542 17.8967Z"
                      stroke="white"
                      strokeWidth="1.56686"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M9.95312 6.92871V10.0624"
                      stroke="white"
                      strokeWidth="1.56686"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M9.95312 13.1963H9.96253"
                      stroke="white"
                      strokeWidth="1.56686"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>{" "}
                  Please Ask AngelAi a Request
                </div>
              )}

              {/* Input box with background effects */}
              <div
                className={`search-box ${!checkboxChecked ? "disabled" : ""}`}
              >
                <div className="search-box-before"></div>
                {OptimizedTextArea}
              </div>

              {/* Send button container */}
              <div className="consent-part" style={consentPartStyle}>
                {/* Loader or Submit Button */}
                {isLoading ? (
                  <Spinner
                    animation="border"
                    role="status"
                    size="sm"
                    style={{
                      alignItems: "center",
                      display: "flex",
                      marginTop: "15px",
                      height: "30px",
                      width: "30px",
                    }}
                  >
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                ) : (
                  <div className="send-button" onClick={handleSubmit}>
                    <svg
                      width={isMobile ? "20" : "20"}
                      height={isMobile ? "25" : "25"}
                      viewBox="0 0 75 83"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M27.1706 38.1746C22.5706 38.1746 17.9706 38.1346 13.3606 38.1846C9.46063 38.2246 6.89063 36.4446 6.03063 32.6646C4.40063 25.5446 2.80063 18.4046 1.45063 11.2146C0.760633 7.57462 1.86063 4.30462 5.09063 2.06462C8.45063 -0.265378 11.5606 0.734622 14.8106 2.58462C32.6306 12.7246 50.4806 22.8046 68.3906 32.7946C71.9606 34.7846 74.2406 37.4446 74.2706 41.5746C74.3006 45.7446 71.9406 48.3346 68.3706 50.3146C50.3306 60.3146 32.3406 70.3946 14.3706 80.5246C11.0306 82.4046 7.75063 83.7046 4.36063 80.9746C1.13063 78.3646 0.360633 74.9046 1.33063 70.9146C2.89063 64.5346 4.23063 58.0946 5.90063 51.7346C7.38063 46.1046 8.86063 45.1046 14.5306 45.0846C22.7406 45.0546 30.9506 45.0446 39.1606 45.0246C39.9106 45.0246 40.6706 45.1146 41.4006 44.9946C43.5106 44.6546 46.3406 45.0546 46.3306 41.7846C46.3206 38.6246 43.9606 38.1346 41.3606 38.1446C36.6306 38.1746 31.9106 38.1446 27.1806 38.1446V38.1646L27.1706 38.1746Z"
                        fill="white"
                      ></path>
                    </svg>
                  </div>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default SearchBar;
